import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Download,
  Printer,
  Link2,
  Search,
  Menu,
  FileText,
  ChevronRight,
  Check,
  HelpCircle,
  Mail,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  sections,
  documentInfo,
  versionDescriptions,
  getFilteredSections,
  type VersionType,
  type Section,
} from "@/data/poTermsContent";

const POTerms = () => {
  const [activeVersion, setActiveVersion] = useState<VersionType>("full");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState<string>("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const filteredSections = getFilteredSections(activeVersion).filter((section) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const matchesTitle = section.title.toLowerCase().includes(query);
    const matchesContent = section.content.toLowerCase().includes(query);
    const matchesSubsections = section.subsections?.some(
      (sub) =>
        sub.title.toLowerCase().includes(query) ||
        sub.content.toLowerCase().includes(query)
    );
    return matchesTitle || matchesContent || matchesSubsections;
  });

  // Intersection Observer for active section tracking
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-100px 0px -66% 0px" }
    );

    const sectionElements = document.querySelectorAll("[data-section]");
    sectionElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [filteredSections]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    toast({
      title: "Preparing PDF",
      description: "Use your browser's print dialog to save as PDF",
    });
    setTimeout(() => window.print(), 500);
  };

  const handleCopyLink = (sectionId: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${sectionId}`;
    navigator.clipboard.writeText(url);
    toast({
      title: "Link Copied",
      description: "Section link copied to clipboard",
    });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setMobileMenuOpen(false);
    }
  };

  const TableOfContents = ({ isMobile = false }: { isMobile?: boolean }) => (
    <nav className={isMobile ? "" : "space-y-1"}>
      {filteredSections.map((section) => (
        <button
          key={section.id}
          onClick={() => scrollToSection(section.id)}
          className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
            activeSection === section.id
              ? "bg-wle-blue/10 text-wle-blue font-medium"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
        >
          <span className="font-mono text-xs opacity-60">{section.number}</span>
          <span className="truncate">{section.title}</span>
        </button>
      ))}
    </nav>
  );

  const renderSectionContent = (section: Section) => (
    <div className="space-y-4">
      {section.content && (
        <p className="text-muted-foreground leading-relaxed">{section.content}</p>
      )}
      
      {section.listItems && (
        <ul className="space-y-2 ml-4">
          {section.listItems.map((item, idx) => (
            <li key={idx} className="text-muted-foreground flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-1 text-wle-blue shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      {section.subsections?.map((sub) => (
        <div key={sub.id} className="mt-6 pl-4 border-l-2 border-wle-blue/20">
          <h4 className="font-semibold text-foreground mb-2">{sub.title}</h4>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
            {sub.content}
          </p>
          {sub.listItems && (
            <ul className="space-y-2 mt-3 ml-4">
              {sub.listItems.map((item, idx) => (
                <li key={idx} className="text-muted-foreground flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 mt-1 text-wle-blue shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Purchase Order Terms & Conditions | WLE Technology</title>
        <meta
          name="description"
          content="WLE Technology Purchase Order Terms and Conditions for suppliers and vendors. Review our complete PO terms including quality assurance, delivery, and warranty requirements."
        />
      </Helmet>

      <div className="min-h-screen bg-background print:bg-white">
        <div className="print:hidden">
          <Navigation />
        </div>

        <main className="pt-20 pb-16 print:pt-0">
          <div className="container max-w-7xl">
            {/* Header */}
            <div className="mb-8 print:mb-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 print:hidden">
                <FileText className="h-4 w-4" />
                <span>Legal Documents</span>
                <ChevronRight className="h-4 w-4" />
                <span>Purchase Order Terms</span>
              </div>

              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-2">
                    Purchase Order Terms & Conditions
                  </h1>
                  <p className="text-muted-foreground">
                    {documentInfo.company}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <Badge variant="outline" className="text-xs">
                      Version {documentInfo.version}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      Last Updated: {documentInfo.lastUpdated}
                    </Badge>
                  </div>
                </div>

                {/* Quick Actions - Desktop */}
                <div className="flex items-center gap-2 print:hidden">
                  <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={handlePrint}>
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </div>
              </div>
            </div>

            {/* Supplier Type Guide */}
            <Card className="mb-8 border-wle-blue/20 bg-wle-blue/5 print:hidden">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-wle-blue/10 rounded-lg">
                    <HelpCircle className="h-6 w-6 text-wle-blue" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-3">
                      Which version applies to you?
                    </h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {(Object.entries(versionDescriptions) as [VersionType, typeof versionDescriptions.full][]).map(
                        ([key, desc]) => (
                          <button
                            key={key}
                            onClick={() => setActiveVersion(key)}
                            className={`text-left p-3 rounded-lg border transition-all ${
                              activeVersion === key
                                ? "border-wle-blue bg-wle-blue/10"
                                : "border-border hover:border-wle-blue/50"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {activeVersion === key && (
                                <Check className="h-4 w-4 text-wle-blue" />
                              )}
                              <span className="font-medium text-foreground">
                                {desc.title}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {desc.description}
                            </p>
                          </button>
                        )
                      )}
                    </div>
                    <div className="mt-4 pt-4 border-t border-border">
                      <a
                        href="/contact"
                        className="inline-flex items-center gap-2 text-sm text-wle-blue hover:underline"
                      >
                        <Mail className="h-4 w-4" />
                        Not sure? Contact our procurement team
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Controls Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-8 print:hidden">
              {/* Version Tabs */}
              <Tabs
                value={activeVersion}
                onValueChange={(v) => setActiveVersion(v as VersionType)}
                className="w-full sm:w-auto"
              >
                <TabsList className="grid grid-cols-3 w-full sm:w-auto">
                  <TabsTrigger value="full" className="text-xs sm:text-sm">
                    Full
                  </TabsTrigger>
                  <TabsTrigger value="standard" className="text-xs sm:text-sm">
                    Standard
                  </TabsTrigger>
                  <TabsTrigger value="basic" className="text-xs sm:text-sm">
                    Basic
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search clauses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Mobile TOC Button */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden">
                    <Menu className="h-4 w-4 mr-2" />
                    Contents
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetTitle className="text-lg font-semibold mb-4">
                    Table of Contents
                  </SheetTitle>
                  <ScrollArea className="h-[calc(100vh-8rem)]">
                    <TableOfContents isMobile />
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            </div>

            {/* Main Content Area */}
            <div className="flex gap-8">
              {/* Sticky Sidebar - Desktop */}
              <aside className="hidden lg:block w-72 shrink-0 print:hidden">
                <div className="sticky top-24">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Table of Contents
                  </h3>
                  <ScrollArea className="h-[calc(100vh-12rem)]">
                    <TableOfContents />
                  </ScrollArea>
                </div>
              </aside>

              {/* Content */}
              <div ref={contentRef} className="flex-1 min-w-0">
                {filteredSections.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No results found</h3>
                      <p className="text-muted-foreground">
                        Try adjusting your search query or changing the version filter.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <Accordion
                    type="multiple"
                    defaultValue={filteredSections.map((s) => s.id)}
                    className="space-y-4"
                  >
                    {filteredSections.map((section) => (
                      <AccordionItem
                        key={section.id}
                        value={section.id}
                        id={section.id}
                        data-section
                        className="border rounded-lg bg-card px-6 print:border-gray-300"
                      >
                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="flex items-center gap-4 text-left">
                            <Badge
                              variant={section.isAppendix ? "secondary" : "outline"}
                              className="font-mono text-xs shrink-0"
                            >
                              {section.number}
                            </Badge>
                            <span className="font-semibold text-foreground">
                              {section.title}
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-6">
                          <div className="flex items-center gap-2 mb-4 print:hidden">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyLink(section.id)}
                              className="text-xs text-muted-foreground hover:text-foreground"
                            >
                              <Link2 className="h-3 w-3 mr-1" />
                              Copy link
                            </Button>
                          </div>
                          {renderSectionContent(section)}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}

                {/* Version Note */}
                {activeVersion !== "full" && (
                  <Card className="mt-8 border-amber-500/20 bg-amber-500/5 print:hidden">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Note:</strong> You are viewing the{" "}
                        <span className="font-medium text-wle-blue">
                          {versionDescriptions[activeVersion].title}
                        </span>
                        . Some clauses have been hidden for brevity.{" "}
                        <button
                          onClick={() => setActiveVersion("full")}
                          className="text-wle-blue hover:underline"
                        >
                          View full version
                        </button>
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Contact Section */}
                <Card className="mt-8 print:hidden">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-2">
                      Questions about these terms?
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      If you have any questions or need clarification on any clause, please
                      contact our procurement team.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild>
                        <a href="/contact">Contact Procurement</a>
                      </Button>
                      <Button variant="outline" asChild>
                        <a href="mailto:procurement@wle.com.my">
                          <Mail className="h-4 w-4 mr-2" />
                          Email Us
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </main>

        <div className="print:hidden">
          <Footer />
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:bg-white {
            background: white !important;
          }
          .print\\:pt-0 {
            padding-top: 0 !important;
          }
          .print\\:mb-4 {
            margin-bottom: 1rem !important;
          }
          .print\\:border-gray-300 {
            border-color: #d1d5db !important;
          }
          [data-state="closed"] > [data-radix-accordion-content] {
            display: block !important;
            height: auto !important;
          }
        }
      `}</style>
    </>
  );
};

export default POTerms;
